/**
 * Public Equipment page — showcases recommended equipment for Garage Gym and Home Gym setups.
 * Begin Gym equipment is proprietary to upcoming OKC facility locations.
 * Equipment data is loaded from Firestore (synced from JSON via Admin).
 */
import React, { useState, useEffect, useMemo } from 'react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InfoModal from '../components/InfoModal';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import HeroImage from '../assets/images/equipment-hero.png';
import '../styles/Equipment.css';
import '../styles/App.css';

function Equipment() {
  const [equipment, setEquipment] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const [eqSnap, tierSnap] = await Promise.all([
          getDocs(collection(db, 'equipment')),
          getDocs(collection(db, 'equipment_tiers')),
        ]);
        const eqList = eqSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const tierList = tierSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        console.log('[Equipment] Loaded', eqList.length, 'equipment items from Firestore');
        console.log('[Equipment] Loaded', tierList.length, 'tiers from Firestore');
        console.log('[Equipment] Equipment IDs:', eqList.map((e) => e.id));
        console.log('[Equipment] Tier IDs:', tierList.map((t) => t.id));
        setEquipment(eqList);
        setTiers(tierList);
      } catch (err) {
        console.error('[Equipment] Firestore fetch failed:', err);
        setError(err.message || 'Failed to load equipment data.');
      } finally {
        setLoaded(true);
      }
    }
    fetchData();
  }, []);

  const equipmentById = useMemo(() => {
    const map = {};
    equipment.forEach((e) => { map[e.id] = e; });
    return map;
  }, [equipment]);

  const garageTier = tiers.find((t) => t.id === 'garage_gym');
  const homeTier = tiers.find((t) => t.id === 'home_gym');
  const beginTier = tiers.find((t) => t.id === 'begin_gym');

  // Deduplicate equipment across all areas for a tier
  const getUniqueTierEquipment = (tier) => {
    if (!tier) return [];
    const seen = new Set();
    const result = [];
    const missing = [];
    (tier.facility_areas || []).forEach((area) => {
      (area.equipment || []).forEach((eqId) => {
        if (!seen.has(eqId)) {
          seen.add(eqId);
          const eq = equipmentById[eqId];
          if (eq) {
            const overrides = tier.overrides?.[eqId] || {};
            result.push({ ...eq, ...overrides });
          } else {
            missing.push(eqId);
          }
        }
      });
    });
    if (missing.length > 0) {
      console.warn(`[Equipment] Tier "${tier.id}" references missing equipment IDs:`, missing);
    }
    console.log(`[Equipment] Tier "${tier.id}" resolved ${result.length} items, ${missing.length} missing`);

    // Sort by manufacturer priority: Nike → Rogue → Forza → Other
    const MFR_ORDER = { nike: 0, rogue: 1, forza: 2 };
    result.sort((a, b) => {
      const mfrA = (a.manufacturer || '').toLowerCase().split(/\s/)[0];
      const mfrB = (b.manufacturer || '').toLowerCase().split(/\s/)[0];
      const orderA = MFR_ORDER[mfrA] ?? 3;
      const orderB = MFR_ORDER[mfrB] ?? 3;
      if (orderA !== orderB) return orderA - orderB;
      return (a.name || '').localeCompare(b.name || '');
    });

    return result;
  };

  const garageEquipment = getUniqueTierEquipment(garageTier);
  const homeEquipment = getUniqueTierEquipment(homeTier);
  const beginEquipment = getUniqueTierEquipment(beginTier);

  const beginGymInfoItems = [
    { title: 'Nike Strength', description: 'Equipped Gym' },
    { title: 'Media Wall', description: 'Stream Virtual Classes' },
    { title: 'Court', description: 'Regulation Volleyball' },
    { title: 'Turf', description: '40 yds x 40 yds' },
  ];

  const formatWeights = (item) => {
    if (item.weights_lb && item.weights_lb.length > 0) {
      return item.weights_lb.join(', ') + ' lb';
    }
    if (item.resistance_levels && item.resistance_levels.length > 0) {
      return item.resistance_levels.map((l) => l.charAt(0).toUpperCase() + l.slice(1)).join(', ');
    }
    if (item.heights_in && item.heights_in.length > 0) {
      return item.heights_in.map((h) => h + '"').join(', ');
    }
    return null;
  };

  return (
    <div className="home">
      <SEO
        title="Equipment"
        description="Recommended equipment for your garage gym or home gym setup. Build your training space with the same gear used in Begin Strength programs."
        path="/equipment"
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="equipment-hero">
          <div className="equipment-hero-content">
            <h2>
              Your<br /><span style={{ color: '#FF0000' }}>Equipment</span>
            </h2>
            <p>
              Build your training space with the gear our programs are designed around.
              Whether you're setting up a garage gym or working out at home, here's what we recommend.
            </p>
            <p className="equipment-hero-sponsor">
              Begin Strength is proudly sponsored by and an exclusive partner of <strong>Nike Strength</strong>.
            </p>
            <a
              href="https://nikestrength.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="primary-button"
            >
              Shop Nike Strength
            </a>
          </div>
          <div className="equipment-hero-image-container">
            <div className="equipment-hero-image-wrapper">
              <img src={HeroImage} alt="Training at Begin Strength" className="equipment-hero-image-img" />
              <div className="red-border"></div>
              <div className="white-border"></div>
            </div>
          </div>
        </section>

        {error && (
          <section className="equipment-tier-section">
            <div className="equipment-section-inner">
              <p style={{ color: '#FF4444', textAlign: 'center', padding: '40px 0' }}>
                {error}
              </p>
            </div>
          </section>
        )}

        {/* Home Gym */}
        <section className="equipment-tier-section">
          <div className="equipment-section-inner">
            <div className="equipment-tier-header">
              <h2>
                Home <span style={{ color: '#FF0000' }}>Gym</span>
              </h2>
              <p>
                {homeTier?.description || "Ideal for longevity workouts, HIIT, and SIT—whether you're in a guest bedroom or a dedicated workout space. A compact setup that fits smaller spaces and delivers results."}
              </p>
            </div>
            {!loaded ? (
              <p className="equipment-loading">Loading equipment…</p>
            ) : (
              <div className="equipment-grid">
                {homeEquipment.map((item) => (
                  <EquipmentCard key={item.id} item={item} formatWeights={formatWeights} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Garage Gym */}
        <section className="equipment-tier-section equipment-tier-section--alt">
          <div className="equipment-section-inner">
            <div className="equipment-tier-header">
              <h2>
                Garage <span style={{ color: '#FF0000' }}>Gym</span>
              </h2>
              <p>
                {garageTier?.description || 'A bigger investment with more weights and versatility. Every workout in the Begin Strength app is designed to work with this equipment.'}
              </p>
            </div>
            {!loaded ? (
              <p className="equipment-loading">Loading equipment…</p>
            ) : (
              <div className="equipment-grid">
                {garageEquipment.map((item) => (
                  <EquipmentCard key={item.id} item={item} formatWeights={formatWeights} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Divider — Begin Gym facility highlights */}
        <InfoModal items={beginGymInfoItems} />

        {/* Begin Strength Gym */}
        <section className="equipment-tier-section">
          <div className="equipment-section-inner">
            <div className="equipment-tier-header">
              <h2>
                Begin Strength <span style={{ color: '#FF0000' }}>Gym</span>
              </h2>
              <p className="equipment-tier-disclaimer">
                The Begin Gym setup is proprietary to Begin Strength gym locations, coming soon to Oklahoma City. If you are using the app, Begin Gym will have equipment found at most big box gyms.
              </p>
            </div>
            {!loaded ? (
              <p className="equipment-loading">Loading equipment…</p>
            ) : (
              <div className="equipment-grid">
                {beginEquipment.map((item) => (
                  <EquipmentCard key={item.id} item={item} formatWeights={formatWeights} />
                ))}
              </div>
            )}
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}

function EquipmentCard({ item, formatWeights }) {
  const weights = formatWeights(item);
  const hasLink = !!item.product_url;

  const cardContent = (
    <>
      <div className="equipment-card-media">
        {item.photo_url ? (
          <img src={item.photo_url} alt={item.name} className="equipment-card-img" />
        ) : (
          <div className="equipment-card-placeholder">
            <span>{item.category?.replace(/_/g, ' ') || 'Equipment'}</span>
          </div>
        )}
      </div>
      <div className="equipment-card-body">
        <h4 className="equipment-card-name">{item.name}</h4>
        {item.manufacturer && (
          <span className="equipment-card-brand">{item.manufacturer}</span>
        )}
        {weights && (
          <span className="equipment-card-weights">{weights}</span>
        )}
        {item.description && (
          <p className="equipment-card-desc">{item.description}</p>
        )}
        {hasLink && (
          <span className="equipment-card-link-label">
            Shop at {item.product_retailer || 'retailer'} →
          </span>
        )}
      </div>
    </>
  );

  if (hasLink) {
    return (
      <a
        href={item.product_url}
        target="_blank"
        rel="noopener noreferrer"
        className="equipment-card equipment-card--linked"
      >
        {cardContent}
      </a>
    );
  }

  return <div className="equipment-card">{cardContent}</div>;
}

export default Equipment;
